"use client";
import React, { useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useDialog } from '@/hooks/useDialog';
import solvenciaApi from "@/api/Solvencia";
import Breadcrumb from "@/components/Breadcrumbs/Breadcrumb";

import Busqueda from "./components/Busqueda";
import ViewerPdf from "./components/Viewer";

import { GrDocumentPdf } from "react-icons/gr";
import { IoMdRefresh } from "react-icons/io";

const Solvencia = () => {
  const { user, loading: userLoading } = useAuth();
  const { show } = useDialog();
  const [esSolvente, setEsSolvente] = useState(false);
  const [idPersona, setIdPersona] = useState(null);
  const [dataSolvente, setDataSolvente] = useState(null);
  const [dataCertificado, setDataCertificado] = useState('');
  const [resetForm, setResetForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const prepareData = useCallback(async (cedula, captchaToken) => {
    setIsLoading(true);
    setEsSolvente(false);
    setIdPersona(null);
    setDataSolvente(null);
    setDataCertificado('');
    try {
      const resp = await solvenciaApi.getEsSolvente(cedula, captchaToken);
      if (resp.status != 200) {
        show({ type: 'warning', title: resp.data.error, message: resp.data.message })
        return
      }
      setEsSolvente(resp.data.data.solvente);
      if (!resp.data.data.solvente) {
        setDataSolvente(resp.data.data.Datos);
      } else {
        setIdPersona(resp.data.data.idpersona);
        setDataSolvente({
          "ES SOLVENTE": [
            resp.data.data.message
          ]
        })
      }
    } catch (error) {
      if (error.response?.data) {
        show({
          type: "warning",
          title: error.response.data.error ?? "Error",
          message: error.response.data.msg ?? "Ocurrio un error al realizar la solicitud, intente de nuevo por favor",
        });
      } else {
        const { name, message } = error;
        show({ type: "error", title: name, message: message });
      }
      setEsSolvente(false);
      setDataSolvente('');
    } finally {
      setIsLoading(false);
    }
  }, [show]);

  const prepareCertificado = useCallback(async (idPersona) => {
    setIsLoading(true);
    try {
      const resp = await solvenciaApi.getCearSolvencia(idPersona);
      if (resp.status != 200) {
        show({ type: 'warning', title: resp.data.error, message: resp.data.msg })
        return
      }
      setDataCertificado(resp.data.data.Certificado);
    } catch (error) {
      if (error.response?.data) {
        show({
          type: "warning",
          title: error.response.data.error,
          message: error.response.data.message,
        });
      } else {
        const { name, message } = error;
        show({ type: "error", title: name, message: message });
      }
      setDataCertificado('');
    } finally {
      setIsLoading(false);
    }
  }, [show]);

  const handleReset = useCallback(() => {
    setResetForm(true);
    setDataSolvente(null);
    setDataCertificado('');
    setTimeout(() => setResetForm(false), 100); // Resetear el flag después de un breve tiempo
  }, []);

  // useEffect(() => {
  //   if (userLoading === false && user) {
  //     setIsLoading(true)
  //     prepareData(user.cedularuc, captchaToken);
  //   }
  // }, [prepareData, user, userLoading, captchaToken]);

  return (
    <>
      <Breadcrumb pageName="Generación de Certificado de Solvencia" />
      <div className="grid grid-cols-1 gap-4">
        {!dataSolvente ? (
          userLoading ? (
            <div className="flex justify-center items-center">
              <span className="loading loading-spinner"></span>
            </div>
          ) : (
            <Busqueda key={resetForm ? "reset-on" : "reset-off"}
              isLoading={isLoading}
              onSearch={prepareData}
              user={user}
              userLoading={userLoading} />
          )
        ) : (
          <>
            <div className="rounded-sm border border-stroke bg-white p-4 shadow-default dark:border-strokedark dark:bg-boxdark">
              {Object.entries(dataSolvente).map(([categoria, mensajes]) => (
                <div key={categoria} className="mb-4">
                  <h3 className="mb-2 text-lg font-semibold text-black dark:text-white">{categoria}</h3>
                  <ul className="list-inside list-disc">
                    {mensajes.map((mensaje, index) => (
                      <li key={index} className="text-sm text-body dark:text-bodydark">{mensaje}</li>
                    ))}
                  </ul>
                </div>
              ))}
              <div className="flex justify-center gap-4">
                {esSolvente && !dataCertificado && (
                  <button
                    onClick={() => prepareCertificado(idPersona)}
                    className="btn rounded-lg btn-primary px-4 py-2"
                    disabled={isLoading}
                  >
                    {isLoading && <span className="loading loading-spinner"></span>}
                    <GrDocumentPdf size={24} />
                    Generar Certificado
                  </button>
                )}
                <button
                  onClick={handleReset}
                  className="btn rounded-lg btn-secundary px-4 py-2"
                  disabled={isLoading}
                >
                  <IoMdRefresh size={24} />
                  Hacer otra consulta
                </button>
              </div>
            </div>
            {dataCertificado && (
              <ViewerPdf isLoading={isLoading} base64={dataCertificado} />
            )}
          </>
        )}
      </div>
    </>
  );
};

export default Solvencia;
